import { supabase } from './supabase';
import { API_BASE } from './apiConfig';
import { getMetaTrackingContext } from './checkoutContext';
import { phFailure, phInitiateCheckout } from './posthog';
import { trackInitiateCheckout } from './fbpixel';
import { trackRedditAddToCart } from './redditPixel';

/* Price of one more bill. It's $129 for everyone now, annual members too
   (it used to be $59 on the Annual Plan). Takes the plan so callers don't
   change if that ever differs again. Keep in sync with createAddonCheckout. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const addonPriceFor = (_plan?: string | null) => 129;

/* Starts a Stripe checkout for one more bill. Same as the "Add a Bill"
   button on /plan, but returnTo lets the add-bill page bring people
   straight back to the upload form once they've paid.
   Returns an error message, or null once the browser is on its way to Stripe. */
export async function startAddonCheckout(opts: {
  plan?: string | null;
  email?: string | null;
  userId: string;
  returnTo: '/plan' | '/add-bill';
}): Promise<string | null> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;

  const res = await fetch(`${API_BASE}/stripe/create-addon-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ ...getMetaTrackingContext(), returnTo: opts.returnTo }),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.url) {
    phFailure({ step: 'checkout_create_addon', message: data.error, code: res.status });
    return data.error || 'Could not start checkout. Please try again.';
  }

  const value = addonPriceFor(opts.plan);
  const eventID = crypto.randomUUID();
  phInitiateCheckout('addon', { flow: 'addon_bill', value, plan: opts.plan ?? undefined });
  void trackInitiateCheckout({ eventID, value, contentName: 'addon_bill', email: opts.email || undefined, externalId: opts.userId });
  trackRedditAddToCart({ eventID, value, contentName: 'addon_bill', email: opts.email || undefined, externalId: opts.userId });

  window.location.href = data.url;
  return null;
}

/* After Stripe sends someone back with ?addon=success&session_id=…, this
   confirms the payment and adds the bill to their plan (the webhook does
   the same, whichever lands first). Returns the new bills_limit, or null. */
export async function verifyAddonPayment(sessionId: string): Promise<{ billsLimit: number; plan?: string } | null> {
  try {
    const res = await fetch(`${API_BASE}/stripe/verify-addon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    const data = await res.json();
    if (!res.ok || !data.subscription) return null;
    return { billsLimit: data.subscription.bills_limit, plan: data.subscription.plan };
  } catch {
    return null;
  }
}
